<template>
  <div class="trade-typeahead">
    <div ref="wrapper" class="picker-wrapper">
      <div
        class="token-field"
        :class="{ focused: pickerOpen }"
        @click="focusInput"
      >
        <span class="field-label">Trades</span>

        <span v-for="trade in modelValue" :key="trade" class="token-pill">
          {{ trade }}
          <button
            type="button"
            class="token-remove"
            aria-label="Remove trade"
            @click.stop="removeTrade(trade)"
          >
            <PhX :size="10" weight="bold" />
          </button>
        </span>

        <input
          :id="inputId"
          ref="input"
          v-model="query"
          type="text"
          role="combobox"
          aria-label="Search trades"
          aria-autocomplete="list"
          :aria-expanded="pickerOpen"
          :aria-controls="listboxId"
          :placeholder="inputPlaceholder"
          class="token-input"
          @focus="openPicker"
          @keydown="onKeyDown"
        />

        <button
          v-if="hasQuery"
          type="button"
          class="clear-btn"
          @click.stop="clearQuery"
        >
          Clear
        </button>

        <button
          type="button"
          class="chevron-toggle"
          :aria-label="pickerOpen ? 'Close trade search' : 'Open trade search'"
          @click.stop="togglePicker"
        >
          <PhCaretUp v-if="pickerOpen" :size="16" weight="bold" />
          <PhCaretDown v-else :size="16" weight="bold" />
        </button>
      </div>

      <div v-if="pickerOpen" class="dropdown-panel">
        <div class="dropdown-header">
          <div class="header-label-group">
            <span class="section-label">{{ resultsLabel }}</span>
            <span class="results-count">{{ resultsCount }}</span>
          </div>
          <button
            type="button"
            class="close-pill"
            aria-label="Close trade search"
            @click="closePicker"
          >
            Close
            <PhX :size="11" weight="bold" />
          </button>
        </div>

        <div
          :id="listboxId"
          role="listbox"
          aria-label="Trades"
          class="results-scroll"
          @scroll="onResultsScroll"
        >
          <div v-if="catalogLoading" class="empty-state">Loading trades…</div>

          <template v-else-if="browseMode">
            <div v-if="commonOptions.length" class="option-group">
              <div class="group-heading common-heading">
                Most common near you
              </div>
              <div class="option-list">
                <button
                  v-for="trade in commonOptions"
                  :key="trade"
                  type="button"
                  role="option"
                  :aria-selected="isSelected(trade)"
                  class="option-pill common"
                  :class="{ selected: isSelected(trade) }"
                  @click="pickTrade(trade)"
                >
                  {{ trade }}
                </button>
              </div>
            </div>
            <div class="option-group">
              <div class="group-heading">
                {{
                  commonOptions.length
                    ? "Everything else, A–Z"
                    : "All trades, A–Z"
                }}
              </div>
              <div class="option-list">
                <button
                  v-for="trade in visibleOtherOptions"
                  :key="trade"
                  type="button"
                  role="option"
                  :aria-selected="isSelected(trade)"
                  class="option-pill"
                  :class="{ selected: isSelected(trade) }"
                  @click="pickTrade(trade)"
                >
                  {{ trade }}
                </button>
              </div>
            </div>
          </template>

          <div v-else-if="searchMode" class="option-list">
            <button
              v-for="trade in visibleMatches"
              :key="trade"
              type="button"
              role="option"
              :aria-selected="isSelected(trade)"
              class="option-pill"
              :class="{ selected: isSelected(trade), common: isPopular(trade) }"
              @click="pickTrade(trade)"
            >
              {{ trade }}
            </button>
          </div>

          <div v-else class="empty-state">{{ emptyLine }}</div>
        </div>

        <div class="dropdown-footer">
          <span class="footer-count">{{ tradeCountLabel }}</span>
          <DefaultButton
            class="done-btn"
            :btnType="['next-btn']"
            labelText="Done"
            @click="closePicker"
          />
        </div>
      </div>

      <span class="visually-hidden" aria-live="polite">{{ resultsCount }}</span>
    </div>

    <div class="helper-line">{{ tradeCountLabel }}</div>
  </div>
</template>

<script>
import { PhX, PhCaretDown, PhCaretUp } from "@phosphor-icons/vue"
import DefaultButton from "@/components/DefaultButton"
import { getCategories } from "@/api/common"

// How many options to mount at a time (see visibleOtherOptions/
// visibleMatches, and GFB-1384). The option list is a wrapped flex
// layout of variable-width pills, not a fixed-height single-column
// list, so it can't be windowed with standard list virtualization --
// this batches the render instead, mounting only this many at once
// and revealing more as the user scrolls, for the same practical
// effect (a bounded number of nodes) without that complexity.
const RESULTS_BATCH_SIZE = 40

export default {
  name: "trade-typeahead",
  components: {
    PhX,
    PhCaretDown,
    PhCaretUp,
    DefaultButton,
  },
  props: {
    // Selected trade labels, in selection order.
    modelValue: {
      type: Array,
      default: () => [],
    },
    // Full trade catalog. Optional — when omitted, the component fetches
    // active trades from GET /api/categories itself (see fetchTrades()).
    allTrades: {
      type: Array,
    },
    // "Most common near you" — expected ~6-10 entries.
    popularTrades: {
      type: Array,
      default: () => [],
    },
    // Trades to pre-select when the caller doesn't already supply an
    // initial modelValue. Empty by default (GFB-1421 -- /find-work now
    // offers its former defaults as one-click suggestions instead); use
    // exact GET /api/categories names. Applied once on creation; later
    // changes to this prop are intentionally ignored so an in-progress
    // user selection is never clobbered.
    defaultTrades: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["update:modelValue"],
  // This app runs Vue 3 under @vue/compat, which defaults custom-component
  // v-model to Vue 2 behavior (a "value"/"input" pair) even when a component
  // declares the Vue 3 "modelValue"/"update:modelValue" convention. Opt this
  // component into real Vue 3 v-model behavior explicitly.
  compatConfig: { COMPONENT_V_MODEL: false },
  data() {
    return {
      query: "",
      // Drives the option list (matches/browseMode/searchMode/etc). Kept
      // separate from `query` so the input itself stays instantly
      // responsive while the (comparatively expensive, ~150-option)
      // list re-render is debounced rather than firing on every
      // keystroke -- see GFB-1383.
      debouncedQuery: "",
      pickerOpen: false,
      fetchedTrades: [],
      catalogLoading: this.allTrades === undefined,
      // How many of otherOptions/matches are currently mounted -- see
      // RESULTS_BATCH_SIZE and onResultsScroll (GFB-1384).
      visibleOtherCount: RESULTS_BATCH_SIZE,
      visibleMatchCount: RESULTS_BATCH_SIZE,
    }
  },
  watch: {
    query(value) {
      clearTimeout(this.searchDebounceTimer)
      // Clearing the query (e.g. via the Clear button, or after picking a
      // trade) should feel instant, not lag behind like typing does.
      if (!value) {
        this.debouncedQuery = value
        return
      }
      this.searchDebounceTimer = setTimeout(() => {
        this.debouncedQuery = value
      }, 150)
    },
    // A new search result set should start from the first batch again,
    // not stay scrolled into whatever batch the previous query reached.
    debouncedQuery() {
      this.visibleMatchCount = RESULTS_BATCH_SIZE
    },
    pickerOpen(isOpen) {
      if (isOpen) {
        this.visibleOtherCount = RESULTS_BATCH_SIZE
        this.visibleMatchCount = RESULTS_BATCH_SIZE
      }
    },
  },
  computed: {
    // The effective catalog: the allTrades prop when the caller supplies
    // one, otherwise whatever fetchTrades() has loaded so far (empty until
    // that resolves).
    resolvedAllTrades() {
      return this.allTrades !== undefined ? this.allTrades : this.fetchedTrades
    },
    inputId() {
      return `trade-typeahead-input-${this.$.uid}`
    },
    listboxId() {
      return `trade-typeahead-listbox-${this.$.uid}`
    },
    // Drives the Clear button and input state -- kept on the immediate
    // `query` so it reacts instantly, unlike the (debounced) option list.
    hasQuery() {
      return this.query.length > 0
    },
    hasDebouncedQuery() {
      return this.debouncedQuery.length > 0
    },
    inputPlaceholder() {
      return this.modelValue.length ? "Add another…" : "Search 100+ trades…"
    },
    // Search-mode matches, ranked case-insensitively with starts-with above
    // contains; original catalog order preserved within each group. When the
    // query is empty this instead yields the popular list (filtered against
    // the catalog) — used for browse/search mode detection, not for the
    // browse view's own option lists (those are commonOptions/otherOptions).
    // Reads the debounced query rather than the raw one -- see GFB-1383.
    matches() {
      const q = this.debouncedQuery.trim().toLowerCase()
      const all = this.resolvedAllTrades
      if (!q) {
        return this.popularTrades.filter((t) => all.includes(t))
      }
      const starts = all.filter((t) => t.toLowerCase().startsWith(q))
      const rest = all.filter(
        (t) => !t.toLowerCase().startsWith(q) && t.toLowerCase().includes(q)
      )
      return starts.concat(rest)
    },
    commonOptions() {
      return this.popularTrades
    },
    otherOptions() {
      return this.resolvedAllTrades
        .filter((t) => !this.popularTrades.includes(t))
        .slice()
        .sort()
    },
    // Only these get mounted at once -- see RESULTS_BATCH_SIZE.
    visibleOtherOptions() {
      return this.otherOptions.slice(0, this.visibleOtherCount)
    },
    visibleMatches() {
      return this.matches.slice(0, this.visibleMatchCount)
    },
    // With no query, always browse the whole catalog -- don't gate on
    // popularTrades being present (GFB-1389). The "no match" empty state
    // is only for a query that matched nothing.
    browseMode() {
      return !this.hasDebouncedQuery && this.resolvedAllTrades.length > 0
    },
    searchMode() {
      return this.hasDebouncedQuery && this.matches.length > 0
    },
    noResults() {
      return this.matches.length === 0
    },
    resultsLabel() {
      return this.hasDebouncedQuery ? "Matching trades" : "Browse by trade"
    },
    resultsCount() {
      if (this.catalogLoading) {
        return ""
      }
      if (this.hasDebouncedQuery) {
        const n = this.matches.length
        return `${n} ${n === 1 ? "match" : "matches"}`
      }
      return `${this.resolvedAllTrades.length} trades total`
    },
    emptyLine() {
      // Only ever a "no match" message for an actual query -- with no
      // query the browse list shows instead (GFB-1389); this branch is
      // just the rare empty-catalog fallback.
      if (!this.hasDebouncedQuery) {
        return "No trades available right now. Please try again in a moment."
      }
      return `No trade matches “${this.debouncedQuery.trim()}”. Try a shorter word, or pick the closest match from Browse by trade.`
    },
    tradeCountLabel() {
      const n = this.modelValue.length
      if (!n) return "Pick at least one trade to keep going."
      return `${n} ${n === 1 ? "trade" : "trades"} selected`
    },
  },
  created() {
    if (this.allTrades === undefined) {
      this.fetchTrades()
    }
    if (this.modelValue.length === 0 && this.defaultTrades.length > 0) {
      this.$emit("update:modelValue", this.defaultTrades.slice())
    }
  },
  mounted() {
    this.onDocumentMouseDown = (e) => {
      if (
        this.pickerOpen &&
        this.$refs.wrapper &&
        !this.$refs.wrapper.contains(e.target)
      ) {
        this.pickerOpen = false
      }
    }
    document.addEventListener("mousedown", this.onDocumentMouseDown)
  },
  beforeUnmount() {
    document.removeEventListener("mousedown", this.onDocumentMouseDown)
    clearTimeout(this.searchDebounceTimer)
  },
  methods: {
    async fetchTrades() {
      try {
        const { data } = await getCategories()
        this.fetchedTrades = (data || []).map((category) => category.name)
      } catch (error) {
        console.error("TradeTypeahead: failed to fetch trades", error)
      } finally {
        this.catalogLoading = false
      }
    },
    // Returns null rather than false when unselected so :aria-selected
    // is omitted from the DOM entirely instead of rendering the literal
    // string "false" on every option (this app's Vue 3 compat mode does
    // not treat plain `false` as "remove the attribute" the way Vue 3
    // normally does) -- with ~150 options, writing that attribute on
    // every one of them on every keystroke was measurably expensive.
    isSelected(trade) {
      return this.modelValue.includes(trade) || null
    },
    isPopular(trade) {
      return this.popularTrades.includes(trade)
    },
    // Reveals the next batch of options as the user scrolls near the
    // bottom of the results -- see RESULTS_BATCH_SIZE (GFB-1384).
    onResultsScroll(e) {
      const el = e.target
      const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 80
      if (!nearBottom) return
      if (
        this.browseMode &&
        this.visibleOtherCount < this.otherOptions.length
      ) {
        this.visibleOtherCount = Math.min(
          this.visibleOtherCount + RESULTS_BATCH_SIZE,
          this.otherOptions.length
        )
      }
      if (this.searchMode && this.visibleMatchCount < this.matches.length) {
        this.visibleMatchCount = Math.min(
          this.visibleMatchCount + RESULTS_BATCH_SIZE,
          this.matches.length
        )
      }
    },
    toggleTrade(trade) {
      const next = this.modelValue.includes(trade)
        ? this.modelValue.filter((t) => t !== trade)
        : this.modelValue.concat(trade)
      this.$emit("update:modelValue", next)
    },
    pickTrade(trade) {
      this.toggleTrade(trade)
      this.query = ""
      this.focusInputField()
    },
    removeTrade(trade) {
      this.toggleTrade(trade)
    },
    focusInputField() {
      this.$nextTick(() => {
        this.$refs.input?.focus()
      })
    },
    focusInput() {
      this.pickerOpen = true
      this.focusInputField()
    },
    openPicker() {
      this.pickerOpen = true
    },
    closePicker(e) {
      e?.stopPropagation?.()
      this.pickerOpen = false
      this.query = ""
    },
    togglePicker(e) {
      e?.stopPropagation?.()
      this.pickerOpen = !this.pickerOpen
    },
    clearQuery() {
      this.query = ""
      this.focusInputField()
    },
    onKeyDown(e) {
      if (e.key === "Escape") {
        this.pickerOpen = false
        return
      }
      if (e.key === "Enter") {
        e.preventDefault()
        // Flush the debounce so Enter acts on what's actually typed,
        // rather than a stale, not-yet-settled match list.
        clearTimeout(this.searchDebounceTimer)
        this.debouncedQuery = this.query
        const top = this.matches[0]
        if (top) {
          this.toggleTrade(top)
          this.query = ""
        }
        return
      }
      if (e.key === "Backspace" && !this.query && this.modelValue.length) {
        this.toggleTrade(this.modelValue[this.modelValue.length - 1])
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.trade-typeahead {
  width: 100%;
}

.picker-wrapper {
  position: relative;
  width: 100%;
}

.token-field {
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  min-height: 52px;
  padding: 10px 40px 10px 12px;
  border: 1px solid var(--gfb-gray-300, #b0b9c9);
  border-radius: var(--gfb-radius-md, 8px);
  background: #fff;
  cursor: text;

  &:focus-within,
  &.focused {
    border-color: var(--gfb-orange-brand-500, #f77f00);
  }
}

.field-label {
  margin-right: 2px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--gfb-gray-400, #8694aa);
}

.token-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 5px 10px 5px 12px;
  background: var(--gfb-orange-brand-50, #ffe2b0);
  border: 1px solid var(--gfb-orange-brand-500, #f77f00);
  border-radius: var(--gfb-radius-pill, 999px);
  color: var(--gfb-blue-brand-800, #003049);
  font-size: var(--gfb-font-size-sm, 14px);
  font-weight: 600;
  white-space: nowrap;
}

.token-remove {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  padding: 0;
  border: none;
  border-radius: var(--gfb-radius-pill, 999px);
  background: var(--gfb-orange-brand-500, #f77f00);
  color: #fff;
  cursor: pointer;
}

.token-input {
  flex: 1;
  min-width: 140px;
  padding: 6px 0;
  border: none;
  outline: none;
  background: transparent;
  font-family: var(--gfb-font-body, inherit);
  font-size: var(--gfb-font-size-sm, 14px);
  color: var(--gfb-color-text, #343946);
}

.clear-btn {
  padding: 0 4px;
  border: none;
  background: none;
  font-size: var(--gfb-font-size-xs, 12px);
  color: var(--gfb-gray-500, #677690);
  cursor: pointer;
}

.chevron-toggle {
  position: absolute;
  right: 8px;
  bottom: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  border-radius: var(--gfb-radius-pill, 999px);
  background: none;
  color: var(--gfb-orange-brand-500, #f77f00);
  cursor: pointer;

  &:hover {
    background: var(--gfb-gray-100, #eceef2);
    color: var(--gfb-orange-brand-500, #f77f00);
  }
}

.dropdown-panel {
  position: absolute;
  left: 0;
  right: 0;
  top: calc(100% + 8px);
  z-index: 20;
  padding: 16px;
  background: #fff;
  border: 1px solid var(--gfb-color-border, #d5d9e2);
  border-radius: var(--gfb-radius-lg, 12px);
  box-shadow: var(--gfb-shadow-lg, 0 18px 30px rgba(17, 24, 39, 0.16));
}

.dropdown-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.header-label-group {
  display: flex;
  align-items: baseline;
  gap: 10px;
}

.section-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--gfb-gray-500, #677690);
}

.results-count {
  font-size: var(--gfb-font-size-xs, 12px);
  color: var(--gfb-gray-400, #8694aa);
}

.close-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 5px 12px;
  border: 1px solid var(--gfb-gray-300, #b0b9c9);
  border-radius: var(--gfb-radius-pill, 999px);
  background: #fff;
  font-size: var(--gfb-font-size-xs, 12px);
  font-weight: 600;
  color: var(--gfb-gray-600, #525e77);
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    border-color: var(--gfb-orange-brand-500, #f77f00);
    color: var(--gfb-orange-brand-500, #f77f00);
  }
}

.results-scroll {
  max-height: 280px;
  overflow-y: auto;
}

.option-group + .option-group {
  margin-top: 18px;
}

.group-heading {
  margin-bottom: 10px;
  font-size: var(--gfb-font-size-xs, 12px);
  font-weight: 600;
  color: var(--gfb-gray-500, #677690);

  &.common-heading {
    font-weight: 700;
    color: var(--gfb-blue-brand-800, #003049);
  }
}

.option-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.option-pill {
  padding: 9px 16px;
  border: 1px solid var(--gfb-gray-200, #d5d9e2);
  border-radius: var(--gfb-radius-pill, 999px);
  background: #fff;
  color: var(--gfb-gray-700, #3a4252);
  font-size: var(--gfb-font-size-sm, 14px);
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    border-color: var(--gfb-orange-brand-300, #ffb14a);
    background: var(--gfb-orange-brand-50, #ffe2b0);
  }

  &.common {
    border-color: var(--gfb-blue-brand-500, #0095e2);

    &:hover {
      background: var(--gfb-blue-brand-50, #e6f6fd);
    }
  }

  &.selected {
    border-color: var(--gfb-orange-brand-500, #f77f00);
    background: var(--gfb-orange-brand-50, #ffe2b0);
    color: var(--gfb-blue-brand-800, #003049);
    font-weight: 600;
  }
}

.empty-state {
  padding: 20px 4px;
  font-size: var(--gfb-font-size-sm, 14px);
  color: var(--gfb-gray-600, #525e77);
  line-height: 1.6;
}

.dropdown-footer {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px 16px;
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid var(--gfb-color-border, #d5d9e2);
}

.footer-count {
  font-size: var(--gfb-font-size-xs, 12px);
  color: var(--gfb-gray-400, #8694aa);
  white-space: nowrap;
}

.done-btn :deep(button) {
  background: var(--gfb-orange-brand-500, #f77f00) !important;
  border-color: var(--gfb-orange-brand-500, #f77f00) !important;
  color: #fff !important;
}

.helper-line {
  margin-top: 12px;
  font-size: var(--gfb-font-size-xs, 12px);
  color: var(--gfb-gray-500, #677690);
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
